/**
 * Ecom wire events — the single owner of the clickout `/dye` types, payload,
 * and params builder. Grep a type string, find everything here.
 */

import type { EventParams, EventParamValue } from '../../core/analytics/track';

/**
 * Wire values are the yoto names — analytics-consumer maps them to
 * ECOM_AFFILIATE_CLICK / ECOM_AFFILIATE_UNIQUE_CLICK by exact string.
 */
export const ECOM_EVENTS = {
  /** Every accepted ecom affiliate link click. */
  click: 'eafc',
  /** First ecom link click in this browser session. */
  uniqueClick: 'eafuc',
} as const;

export type EcomEventType = (typeof ECOM_EVENTS)[keyof typeof ECOM_EVENTS];

export interface ClickoutPayload {
  url: string;
  /** Amazon ASIN when applicable (mirrors yoto product id in bus payloads). */
  productId?: string | number;
  /**
   * Advertiser campaign id for ecom clickout `/dye` (`aci=`).
   *
   * **Allowed values only** (yoto `advertiserCampaignId` parity):
   * - Amazon **ASIN** (e.g. `B08N5WRWNW`) when the click targets a product detail page.
   * - The **full general-link URL** string when the click uses `ecom_general_link` (not an ASIN).
   *
   * **Do not** pass post ids, gallery item ids, channel codes (`ch`), associate tags,
   * click/subtag ids (`ecsid`), internal campaign names, SKUs, or arbitrary labels.
   * Use `postId` for the gallery post (`pid=`), `ecsid` for the Amazon subtag/click id.
   */
  advertiserCampaignId?: string;
  /** Gallery post id (yoto `pid` on /dye — not the ASIN). */
  postId?: string;
  /** `post_items.id` (yoto `iid`). */
  postItemId?: string;
  /** yoto `ipi` — 1-based gallery item index on the page (SDK: render order). */
  itemPageIndex?: number;
  /** yoto `iai` — article-level item ordinal; SDK defaults to render order. */
  itemArticleIndex?: number;
  /** Product / offer market (yoto `ecomProductMarket`, serialized as `epm`). */
  ecomProductMarket?: string;
  /** Gallery item ordinal (yoto `pn` / data-item). */
  pageNumber?: number;
  /**
   * Amazon subid / click id (yoto `ecsid` on `/dye`).
   * Usually the ascsubtag or associate tag applied to the outbound ecom link.
   */
  ecsid?: string;
  /** Amazon parent associate tag when using subtag mode (yoto `ecptg`). */
  ecptg?: string;
  /** Session-scoped clickout count after this click (1-based). */
  sessionClicks?: number;
  extras?: Record<string, unknown>;
}

/** Coerce clickout `extras` into wire-safe string/number/boolean values. */
function flattenExtras(extras: Record<string, unknown> | undefined): EventParams {
  if (!extras) return {};
  const out: EventParams = {};
  for (const [key, value] of Object.entries(extras)) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
    } else {
      out[key] = String(value);
    }
  }
  return out;
}

function nonEmptyString(value: EventParamValue): string | undefined {
  if (value === null || value === undefined) return undefined;
  const s = String(value);
  return s === '' ? undefined : s;
}

/** The complete `/dye` param set for `eafc` / `eafuc`. */
export function buildClickoutParams(payload: ClickoutPayload): EventParams {
  const aci =
    nonEmptyString(payload.advertiserCampaignId) ??
    (payload.productId !== undefined ? String(payload.productId) : undefined);

  return {
    ...(nonEmptyString(payload.ecsid) !== undefined ? { ecsid: String(payload.ecsid) } : {}),
    ...(nonEmptyString(payload.ecptg) !== undefined ? { ecptg: String(payload.ecptg) } : {}),
    epm: payload.ecomProductMarket,
    pn: payload.pageNumber !== undefined ? String(payload.pageNumber) : undefined,
    ...(nonEmptyString(payload.postId) !== undefined ? { pid: String(payload.postId) } : {}),
    ...(aci !== undefined ? { aci } : {}),
    ...(nonEmptyString(payload.postItemId) !== undefined ? { iid: String(payload.postItemId) } : {}),
    ...(payload.itemPageIndex !== undefined ? { ipi: String(payload.itemPageIndex) } : {}),
    ...(payload.itemArticleIndex !== undefined ? { iai: String(payload.itemArticleIndex) } : {}),
    ...flattenExtras(payload.extras),
  };
}
